import { defineStore } from 'pinia'
import { formatPrice } from '@/utils/price'
import { useProductStore } from './products'

export const useCartStore = defineStore('cart', {
  state: () => ({
    items: [],
    loading: false,
    error: null,
    appliedCoupon: null,
    shippingMethod: null,
    // Toast: gevuld nadat een product is toegevoegd; component luistert hierop
    lastAdded: null,   // { product, quantity, timestamp }
  }),

  getters: {
    // Get total number of items in cart
    itemCount: (state) => {
      return state.items.reduce((total, item) => total + item.quantity, 0)
    },

    // Get subtotal (before discounts and shipping)
    subtotal: (state) => {
      return state.items.reduce((total, item) => {
        const price = parseFloat(item.product.sale_price || item.product.price || 0)
        return total + (price * item.quantity)
      }, 0)
    },

    // Get discount amount
    discountAmount: (state) => {
      if (!state.appliedCoupon) return 0
      
      const subtotal = state.items.reduce((total, item) => {
        const price = parseFloat(item.product.sale_price || item.product.price || 0)
        return total + (price * item.quantity)
      }, 0)

      if (state.appliedCoupon.discount_type === 'percent') {
        return (subtotal * parseFloat(state.appliedCoupon.amount)) / 100
      } else {
        return parseFloat(state.appliedCoupon.amount)
      }
    },

    // Get shipping cost
    shippingCost: (state) => {
      if (!state.shippingMethod) return 0
      return parseFloat(state.shippingMethod.cost || 0)
    },

    // Get total (including discounts and shipping)
    total: (state) => {
      const subtotal = state.items.reduce((total, item) => {
        const price = parseFloat(item.product.sale_price || item.product.price || 0)
        return total + (price * item.quantity)
      }, 0)

      let discount = 0
      if (state.appliedCoupon) {
        if (state.appliedCoupon.discount_type === 'percent') {
          discount = (subtotal * parseFloat(state.appliedCoupon.amount)) / 100
        } else {
          discount = parseFloat(state.appliedCoupon.amount)
        }
      }

      const shipping = state.shippingMethod ? parseFloat(state.shippingMethod.cost || 0) : 0

      return Math.max(0, subtotal - discount + shipping)
    },

    // Check if product is in cart
    isInCart: (state) => (productId) => {
      return state.items.some(item => item.product.id === productId)
    },

    // Get cart item by product ID
    getCartItem: (state) => (productId) => {
      return state.items.find(item => item.product.id === productId)
    },

    // Get formatted prices
    formattedPrices: (state) => {
      const format = formatPrice
      
      const subtotal = state.items.reduce((total, item) => {
        const price = parseFloat(item.product.sale_price || item.product.price || 0)
        return total + (price * item.quantity)
      }, 0)

      let discount = 0
      if (state.appliedCoupon) {
        if (state.appliedCoupon.discount_type === 'percent') {
          discount = (subtotal * parseFloat(state.appliedCoupon.amount)) / 100
        } else {
          discount = parseFloat(state.appliedCoupon.amount)
        }
      }

      const shipping = state.shippingMethod ? parseFloat(state.shippingMethod.cost || 0) : 0
      const total = Math.max(0, subtotal - discount + shipping)

      return {
        subtotal: format(subtotal),
        discount: format(discount),
        shipping: format(shipping),
        total: format(total)
      }
    },

    // Check if cart qualifies for free shipping
    qualifiesForFreeShipping: (state) => {
      const FREE_SHIPPING_THRESHOLD = 50 // €50
      const subtotal = state.items.reduce((total, item) => {
        const price = parseFloat(item.product.sale_price || item.product.price || 0)
        return total + (price * item.quantity)
      }, 0)
      return subtotal >= FREE_SHIPPING_THRESHOLD
    },

    // Amount needed for free shipping
    amountForFreeShipping: (state) => {
      const FREE_SHIPPING_THRESHOLD = 50
      const subtotal = state.items.reduce((total, item) => {
        const price = parseFloat(item.product.sale_price || item.product.price || 0)
        return total + (price * item.quantity)
      }, 0)
      const remaining = FREE_SHIPPING_THRESHOLD - subtotal
      return Math.max(0, remaining)
    }
  },

  actions: {
    // Initialize cart from localStorage
   // Initialize cart from localStorage
initCart() {
  if (typeof window === 'undefined') return  // ← SSR guard
  try {
    const stored = localStorage.getItem('plug2charge_cart')
    if (stored) {
      const data = JSON.parse(stored)
      this.items = data.items || []
      this.appliedCoupon = data.appliedCoupon || null
      this.shippingMethod = data.shippingMethod || null
    }
  } catch (error) {
    console.error('Error loading cart from localStorage:', error)
    this.items = []
  }
},

    // Save cart to localStorage
   saveToLocalStorage() {
  if (typeof window === 'undefined') return  // ← SSR guard
  try {
    const data = {
      items: this.items,
      appliedCoupon: this.appliedCoupon,
      shippingMethod: this.shippingMethod,
      updatedAt: new Date().toISOString()
    }
    localStorage.setItem('plug2charge_cart', JSON.stringify(data))
  } catch (error) {
    console.error('Error saving cart to localStorage:', error)
  }
},

    // Add product to cart
    async addToCart(product, quantity = 1, variation = null, setLabel = null) {
      try {
        // Validate product
        if (!product || !product.id) {
          throw new Error('Invalid product')
        }

        // Check stock
        if (product.stock_status === 'outofstock') {
          throw new Error('Product is out of stock')
        }

        if (product.manage_stock && product.stock_quantity !== null) {
          const currentQuantity = this.getCartItem(product.id)?.quantity || 0
          if (currentQuantity + quantity > product.stock_quantity) {
            throw new Error(`Only ${product.stock_quantity} items available`)
          }
        }

        // Check if product already in cart
        const existingItem = this.items.find(item => 
          item.product.id === product.id && 
          (!variation || item.variation?.id === variation?.id)
        )

        if (existingItem) {
          existingItem.quantity += quantity
        } else {
          this.items.push({
            product: product,
            quantity: quantity,
            variation: variation || null,
            setLabel: setLabel || null,
            addedAt: new Date().toISOString()
          })
        }

        this.saveToLocalStorage()

        // Trigger cart toast
        this.lastAdded = { product, quantity, timestamp: Date.now() }

        return true

      } catch (error) {
        this.error = error.message
        throw error
      }
    },

    // Update item quantity
    updateQuantity(productId, quantity, variationId = null) {
      const item = this.items.find(item => 
        item.product.id === productId && 
        (!variationId || item.variation?.id === variationId)
      )

      if (!item) return false

      // Validate quantity
      if (quantity < 1) {
        return this.removeFromCart(productId, variationId)
      }

      // Check stock
      if (item.product.manage_stock && item.product.stock_quantity !== null) {
        if (quantity > item.product.stock_quantity) {
          this.error = `Only ${item.product.stock_quantity} items available`
          return false
        }
      }

      item.quantity = quantity
      this.saveToLocalStorage()
      return true
    },

    // Remove product from cart
    removeFromCart(productId, variationId = null) {
      const index = this.items.findIndex(item => 
        item.product.id === productId && 
        (!variationId || item.variation?.id === variationId)
      )

      if (index > -1) {
        this.items.splice(index, 1)
        this.saveToLocalStorage()
        return true
      }
      return false
    },

    // Clear entire cart
    clearCart() {
      this.items = []
      this.appliedCoupon = null
      this.saveToLocalStorage()
    },

    // Apply coupon code
    async applyCoupon(couponCode) {
      try {
        this.loading = true
        this.error = null

        // Fetch coupon via de interne Express proxy (geen credentials in de browser)
        const response = await fetch(`/_wc/v3/coupons?code=${encodeURIComponent(couponCode)}`)

        if (!response.ok) {
          throw new Error('Ongeldige kortingscode')
        }

        const coupons = await response.json()

        // WooCommerce geeft soms een error-object terug
        if (!Array.isArray(coupons) || coupons.length === 0) {
          throw new Error('Kortingscode niet gevonden')
        }

        const coupon = coupons[0]

        // Verlopen?
        if (coupon.date_expires) {
          if (new Date(coupon.date_expires) < new Date()) {
            throw new Error('Deze kortingscode is verlopen')
          }
        }

        // Minimumbedrag
        if (coupon.minimum_amount && parseFloat(coupon.minimum_amount) > 0) {
          if (this.subtotal < parseFloat(coupon.minimum_amount)) {
            throw new Error(`Minimale bestelling voor deze code: ${formatPrice(coupon.minimum_amount)}`)
          }
        }

        // Maximumbedrag
        if (coupon.maximum_amount && parseFloat(coupon.maximum_amount) > 0) {
          if (this.subtotal > parseFloat(coupon.maximum_amount)) {
            throw new Error(`Maximale bestelling voor deze code: ${formatPrice(coupon.maximum_amount)}`)
          }
        }

        // Maximaal aantal gebruiken bereikt?
        if (coupon.usage_limit && coupon.usage_count >= coupon.usage_limit) {
          throw new Error('Deze kortingscode is niet meer geldig')
        }

        this.appliedCoupon = coupon
        this.saveToLocalStorage()
        return true

      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    // Remove coupon
    removeCoupon() {
      this.appliedCoupon = null
      this.saveToLocalStorage()
    },

    // Set shipping method
    setShippingMethod(method) {
      this.shippingMethod = method
      this.saveToLocalStorage()
    },

    // Refresh product details in cart
    async refreshCartProducts() {
      if (this.items.length === 0) return

      try {
        this.loading = true
        const productStore = useProductStore()

        for (const item of this.items) {
          const product = await productStore.fetchProduct(item.product.id)
          if (product) {
            item.product = product
          }
        }

        this.saveToLocalStorage()

      } catch (error) {
        console.error('Error refreshing cart products:', error)
      } finally {
        this.loading = false
      }
    },

    // Validate cart (check stock, prices)
    async validateCart() {
      const issues = []

      for (const item of this.items) {
        // Check stock status
        if (item.product.stock_status === 'outofstock') {
          issues.push({
            type: 'outofstock',
            productId: item.product.id,
            productName: item.product.name,
            message: `${item.product.name} is out of stock`
          })
        }

        // Check stock quantity
        if (item.product.manage_stock && item.product.stock_quantity !== null) {
          if (item.quantity > item.product.stock_quantity) {
            issues.push({
              type: 'insufficient_stock',
              productId: item.product.id,
              productName: item.product.name,
              available: item.product.stock_quantity,
              requested: item.quantity,
              message: `Only ${item.product.stock_quantity} available for ${item.product.name}`
            })
          }
        }
      }

      return {
        valid: issues.length === 0,
        issues: issues
      }
    },

    // Get cart summary for checkout
    getCheckoutData() {
      return {
        items: this.items.map(item => ({
          product_id: item.product.id,
          variation_id: item.variation?.id || 0,
          quantity: item.quantity,
          name: item.product.name,
          price: item.product.price
        })),
        coupon: this.appliedCoupon?.code || null,
        shipping: this.shippingMethod?.id || null,
        subtotal: this.subtotal,
        discount: this.discountAmount,
        shipping_cost: this.shippingCost,
        total: this.total
      }
    },

    // Export cart (for saving or sharing)
    exportCart() {
      return {
        items: this.items,
        appliedCoupon: this.appliedCoupon,
        shippingMethod: this.shippingMethod,
        totals: {
          subtotal: this.subtotal,
          discount: this.discountAmount,
          shipping: this.shippingCost,
          total: this.total
        },
        exportedAt: new Date().toISOString()
      }
    }
  }
})